// src/hooks/useUserDetails.js
import { useState, useEffect } from 'react';
import {
    fetchUserDetails,
    updateUserStatus,
    deleteUserDocuments,
    updateShopStatus as apiUpdateShopStatus,
    updateUserProfile,
    updateUserContact,
    updateShopDetails,
    updateShopProfile,
    updateShopBankDetails,
    updateShopAddress,
    updateShopMedia,
    updateShopTimings,
    updateShopContact,
        fetchShopCoupons,
        createShopCoupon,
        updateShopCoupon,
        deleteShopCoupon,
} from '../services/api';

export const useUserDetails = (userId) => {
    const [userData, setUserData] = useState(null);
    const [activeShop, setActiveShop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        const loadUser = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await fetchUserDetails(userId);
                setUserData(res.data);
                if (res.data.shop?.length) setActiveShop(res.data.shop[0]);
            } catch (err) {
                setError('Failed to load user details');
                console.error('Error loading user:', err);
            } finally {
                setLoading(false);
            }
        };
        loadUser();
    }, [userId]);

    // User CRUD operations
    const changeUserStatus = async (status) => {
        try {
            setUpdating(true);
            await updateUserStatus(userData.onboardingRequestID, status);
            setUserData((prev) => ({ ...prev, status: status.toUpperCase() }));
        } catch (err) {
            setError('Failed to update user status');
            console.error('Error updating user status:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const resubmitDocuments = async () => {
        try {
            setUpdating(true);
            await deleteUserDocuments(userData._id);
            setUserData((prev) => ({ ...prev, docs: {}, status: 'REJECTED' }));
        } catch (err) {
            setError('Failed to resubmit documents');
            console.error('Error resubmitting documents:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateUserProfileData = async (profileData) => {
        try {
            setUpdating(true);
            const res = await updateUserProfile(userId, profileData);
            setUserData((prev) => ({ ...prev, ...res.data.data }));
            return res.data;
        } catch (err) {
            setError('Failed to update user profile');
            console.error('Error updating user profile:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateUserContactInfo = async (contactData) => {
        try {
            setUpdating(true);
            const res = await updateUserContact(userId, contactData);
            setUserData((prev) => ({ ...prev, ...res.data.data }));
            return res.data;
        } catch (err) {
            const status = err?.response?.status;
            if (status === 400) {
                // surface server message up to UI
                throw err;
            }
            setError('Failed to update contact info');
            console.error('Error updating contact info:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    // Shop CRUD operations
    const changeShopStatus = async (status) => {
        try {
            setUpdating(true);
            const res = await apiUpdateShopStatus(activeShop._id, status);
            const updatedShop = res?.data?.shop || res?.data?.data || res?.data;
            setUserData((prev) => ({
                ...prev,
                shop: prev.shop.map((shop) =>
                    shop._id === updatedShop._id ? updatedShop : shop
                ),
            }));
            setActiveShop(updatedShop);
            return updatedShop;
        } catch (err) {
            setError('Failed to update shop status');
            console.error('Error updating shop status:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateShopData = async (shopData) => {
        try {
            setUpdating(true);
            const res = await updateShopDetails(activeShop._id, shopData);
            const updatedShop = res?.data?.shop || res?.data?.data || res?.data;
            setUserData((prev) => ({
                ...prev,
                shop: prev.shop.map((shop) =>
                    shop._id === updatedShop._id ? updatedShop : shop
                ),
            }));
            setActiveShop(updatedShop);
            return updatedShop;
        } catch (err) {
            setError('Failed to update shop details');
            console.error('Error updating shop details:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateShopProfileData = async (shopName) => {
        try {
            setUpdating(true);
            const res = await updateShopProfile(activeShop._id, shopName);
            const updatedShop = res?.data?.shop || res?.data?.data || res?.data;
            if (updatedShop) {
                setUserData((prev) => ({
                    ...prev,
                    shop: prev.shop.map((shop) =>
                        shop._id === updatedShop._id ? updatedShop : shop
                    ),
                }));
                setActiveShop(updatedShop);
            }
            return res.data;
        } catch (err) {
            setError('Failed to update shop profile');
            console.error('Error updating shop profile:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateShopBankInfo = async (bankDetails) => {
        try {
            setUpdating(true);
            const res = await updateShopBankDetails(activeShop._id, { bankDetails });
            const updatedShop = res?.data?.shop || res?.data?.data || res?.data;
            setUserData((prev) => ({
                ...prev,
                shop: prev.shop.map((shop) =>
                    shop._id === updatedShop._id ? updatedShop : shop
                ),
            }));
            setActiveShop(updatedShop);
            return updatedShop;
        } catch (err) {
            setError('Failed to update shop bank details');
            console.error('Error updating shop bank details:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateShopAddressInfo = async (addressData) => {
        try {
            setUpdating(true);
            const res = await updateShopAddress(activeShop._id, addressData);
            const updatedShop = res?.data?.shop || res?.data?.data || res?.data;
            setUserData((prev) => ({
                ...prev,
                shop: prev.shop.map((shop) =>
                    shop._id === updatedShop._id ? updatedShop : shop
                ),
            }));
            setActiveShop(updatedShop);
            return updatedShop;
        } catch (err) {
            setError('Failed to update shop address');
            console.error('Error updating shop address:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateShopMediaFiles = async (mediaData) => {
        try {
            setUpdating(true);
            const res = await updateShopMedia(activeShop._id, mediaData);
            const updatedShop = res?.data?.shop || res?.data?.data || res?.data;
            setUserData((prev) => ({
                ...prev,
                shop: prev.shop.map((shop) =>
                    shop._id === updatedShop._id ? updatedShop : shop
                ),
            }));
            setActiveShop(updatedShop);
            return updatedShop;
        } catch (err) {
            setError('Failed to update shop media');
            console.error('Error updating shop media:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateShopBusinessHours = async (timingData) => {
        try {
            setUpdating(true);
            const res = await updateShopTimings(activeShop._id, timingData);
            const updatedShop = res?.data?.shop || res?.data?.data || res?.data;
            setUserData((prev) => ({
                ...prev,
                shop: prev.shop.map((shop) =>
                    shop._id === updatedShop._id ? updatedShop : shop
                ),
            }));
            setActiveShop(updatedShop);
            return updatedShop;
        } catch (err) {
            setError('Failed to update shop timings');
            console.error('Error updating shop timings:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const updateShopContactInfo = async (contactData) => {
        try {
            setUpdating(true);
            const res = await updateShopContact(activeShop._id, contactData);
            const updatedShop = res?.data?.shop || res?.data?.data || res?.data;
            setUserData((prev) => ({
                ...prev,
                shop: prev.shop.map((shop) =>
                    shop._id === updatedShop._id ? updatedShop : shop
                ),
            }));
            setActiveShop(updatedShop);
            return updatedShop;
        } catch (err) {
            setError('Failed to update shop contact');
            console.error('Error updating shop contact:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    const clearError = () => setError(null);

    return {
        // State
        userData,
        activeShop,
        setActiveShop,
        loading,
        error,
        updating,
        
        // User operations
        changeUserStatus,
        resubmitDocuments,
        updateUserProfileData,
        updateUserContactInfo,
        
        // Shop operations
        changeShopStatus,
        updateShopData,
        updateShopProfileData,
        updateShopBankInfo,
        updateShopAddressInfo,
        updateShopMediaFiles,
        updateShopBusinessHours,
        updateShopContactInfo,
        // Coupons
        fetchShopCoupons,
        createShopCoupon,
        updateShopCoupon,
        deleteShopCoupon,
        
        // Utility
        clearError,
    };
};